import {
  Banknote,
  Coins,
  KeyRound,
  Layers,
  type LucideIcon,
  Palette,
  QrCode,
  ScanQrCode,
  Wallet,
} from "lucide-react";

import type { ColocaStatusPillProps } from "@/shared/components/data-display/ColocaStatusPill";
import type { OrganizationsTranslations } from "@/shared/lang/interfaces/organizations";
import { OrganizationStatus } from "@/shared/types/organization.entity";

import type {
  GlobalFeature,
  OrganizationFeature,
  PayoutBatchesFilter,
} from "../types/organizationsAdmin.types";

type OrganizationsCopyKey = keyof OrganizationsTranslations;

export const ORGANIZATIONS_PATH = "/app/organizations";

export const ORGANIZATIONS_ADMIN_ENDPOINTS = {
  LIST: "/internal/organizations",
  OVERVIEW: (id: string) => `/internal/organizations/${id}/overview`,
  FEATURES: (id: string) => `/internal/organizations/${id}/features`,
  GLOBAL_FEATURES: "/internal/organizations/features/global",
  CARD_COP_TOP_UPS_ACCESS: "/internal/organizations/features/card-cop-top-ups/access",
} as const;

export const ORGANIZATIONS_PAGE_SIZE = 20;

/** Mirrors `ORGANIZATION_SEARCH_MIN_LENGTH` in coloca-nest: a shorter term is a 400, not a narrower search. */
export const ORGANIZATION_SEARCH_MIN_LENGTH = 3;

export const ORGANIZATION_SEARCH_DEBOUNCE_MS = 350;

export const PAYOUT_BATCHES_FILTERS: readonly PayoutBatchesFilter[] = ["all", "enabled", "disabled"];

export const PAYOUT_BATCHES_FILTER_LABEL_KEYS: Record<PayoutBatchesFilter, keyof OrganizationsTranslations> =
  {
    all: "filter_payout_batches_all",
    enabled: "filter_payout_batches_enabled",
    disabled: "filter_payout_batches_disabled",
  };

export const ORGANIZATION_STATUS_LABEL_KEYS: Record<OrganizationStatus, keyof OrganizationsTranslations> = {
  [OrganizationStatus.ACTIVE]: "status_active",
  [OrganizationStatus.INACTIVE]: "status_inactive",
  [OrganizationStatus.SUSPENDED]: "status_suspended",
  [OrganizationStatus.PENDING]: "status_pending",
};

export const ORGANIZATION_STATUS_PILL: Record<OrganizationStatus, ColocaStatusPillProps["variant"]> = {
  [OrganizationStatus.ACTIVE]: "success",
  [OrganizationStatus.INACTIVE]: "neutral",
  [OrganizationStatus.SUSPENDED]: "danger",
  [OrganizationStatus.PENDING]: "warning",
};

export interface OrganizationFeatureDefinition {
  feature: OrganizationFeature;
  /** The general switch that, while on or off for all, makes this one irrelevant. */
  global?: GlobalFeature;
  icon: LucideIcon;
  labelKey: OrganizationsCopyKey;
  descriptionKey: OrganizationsCopyKey;
  switchAriaKey: OrganizationsCopyKey;
  toastOnKey: OrganizationsCopyKey;
  toastOffKey: OrganizationsCopyKey;
  /** Shown only to the staff the backend allows to see and flip it. */
  allowlisted?: boolean;
}

export const ORGANIZATION_FEATURE_CATALOG: readonly OrganizationFeatureDefinition[] = [
  {
    feature: "payout_batches_enabled",
    global: "payout_batches_enabled",
    icon: Layers,
    labelKey: "col_payout_batches",
    descriptionKey: "feature_payout_batches_description",
    switchAriaKey: "payout_batches_switch_aria",
    toastOnKey: "toast_payout_batches_enabled",
    toastOffKey: "toast_payout_batches_disabled",
  },
  {
    feature: "breb_receiving_key_enabled",
    global: "breb_receiving_key_enabled",
    icon: KeyRound,
    labelKey: "col_breb_key",
    descriptionKey: "feature_breb_key_description",
    switchAriaKey: "breb_key_switch_aria",
    toastOnKey: "toast_breb_key_enabled",
    toastOffKey: "toast_breb_key_disabled",
  },
  {
    feature: "otc_enabled",
    global: "otc_enabled",
    icon: Coins,
    labelKey: "col_crypto",
    descriptionKey: "feature_crypto_description",
    switchAriaKey: "crypto_switch_aria",
    toastOnKey: "toast_crypto_enabled",
    toastOffKey: "toast_crypto_disabled",
  },
  {
    feature: "card_deposits_enabled",
    global: "card_deposits_enabled",
    icon: QrCode,
    labelKey: "col_card_deposits",
    descriptionKey: "feature_card_deposits_description",
    switchAriaKey: "card_deposits_switch_aria",
    toastOnKey: "toast_card_deposits_enabled",
    toastOffKey: "toast_card_deposits_disabled",
  },
  {
    feature: "card_cop_top_ups_enabled",
    global: "card_cop_top_ups_enabled",
    icon: Banknote,
    labelKey: "col_card_cop_top_ups",
    descriptionKey: "feature_card_cop_top_ups_description",
    switchAriaKey: "card_cop_top_ups_switch_aria",
    toastOnKey: "toast_card_cop_top_ups_enabled",
    toastOffKey: "toast_card_cop_top_ups_disabled",
    allowlisted: true,
  },
  {
    feature: "breb_qr_payments_enabled",
    global: "breb_qr_payments_enabled",
    icon: ScanQrCode,
    labelKey: "col_breb_qr_payments",
    descriptionKey: "feature_breb_qr_payments_description",
    switchAriaKey: "breb_qr_payments_switch_aria",
    toastOnKey: "toast_breb_qr_payments_enabled",
    toastOffKey: "toast_breb_qr_payments_disabled",
  },
  {
    // No general switch: the card wallet is a per-organization choice (the organization may turn it on
    // itself), not a rollout. Turning it off while it holds money answers 409, shown as the toast.
    feature: "card_wallet_enabled",
    icon: Wallet,
    labelKey: "col_card_wallet",
    descriptionKey: "feature_card_wallet_description",
    switchAriaKey: "card_wallet_switch_aria",
    toastOnKey: "toast_card_wallet_enabled",
    toastOffKey: "toast_card_wallet_disabled",
  },
  {
    // No general switch: a co-branded design is granted organization by organization. Off, the secure card
    // view goes back to Coloca's design and the saved design waits until it is turned on again.
    feature: "can_customize_card_design",
    icon: Palette,
    labelKey: "col_card_design",
    descriptionKey: "feature_card_design_description",
    switchAriaKey: "card_design_switch_aria",
    toastOnKey: "toast_card_design_enabled",
    toastOffKey: "toast_card_design_disabled",
  },
];

export function visibleFeatureCatalog(canSeeAllowlisted: boolean): readonly OrganizationFeatureDefinition[] {
  return canSeeAllowlisted
    ? ORGANIZATION_FEATURE_CATALOG
    : ORGANIZATION_FEATURE_CATALOG.filter((definition) => !definition.allowlisted);
}
